/**
 * actionCardKit.ts — ONE design system for every send-surface action card
 * (owner directive 2026-08-27, Miles: "much more user friendly, high user
 * experience, more modern sleek action template interface"; scope decision:
 * all send surfaces — the batch card, the single authorize card, and the
 * receipts — as one family).
 *
 * The cards ride `workflowActionButtonsHtml`, which SpecialMessageTile
 * injects UNSANITIZED (only ensureLinksOpenInNewTab's DOMParser round-trip
 * touches it), so real CSS is available: the kit ships a scoped stylesheet
 * (`.p0ac-` prefix) NESTED INSIDE the card's root div — a TOP-LEVEL <style>
 * would be hoisted into <head> by the DOMParser round-trip and dropped, a
 * nested one survives. That stylesheet is what buys the modern feel: hover
 * lift, a rotating chevron, and a gold :focus-visible ring replacing the
 * global `:focus` outline (GlobalStyles.ts) that painted a hard rectangle
 * on every MOUSE click — the "ugly box around hide preview" from Miles's
 * screenshot.
 *
 * FRAMELESS BY DESIGN: these cards render inside SpecialTileContainer
 * (white, 12px radius, hairline border, soft shadow, deep-gold left
 * accent — all three send sourceTags resolve to messageType 'info' →
 * SPECIAL_TILE_COLORS.info = #8A6D2F). The container IS the card; the kit
 * draws only the inside. Never give these messages a sourceTag containing
 * 'success' or 'error' — getMessageTypeFromSourceTag would flip the left
 * accent to green/red, violating the gold-not-green directive.
 *
 * Rules the kit enforces:
 *  - Confirmation is PREST0 GOLD, never green (owner directive 2026-08-25).
 *  - Button labels are the button's SOLE child — no icons inside decision
 *    buttons (sendReceipt.test.ts pins '>send it<'; the legacy ✓-paint
 *    prepends onto innerHTML for non-exempt buttons like retry).
 *  - No hex from normalizeLegacyCtaColors' replace map (#1e40af, #059669,
 *    #7C3AED, #0369a1, #8B5CF6) may appear anywhere in kit output.
 *  - House voice: lowercase copy, authored literally — the kit's scoped
 *    `text-transform: none` (below) means the global sheet no longer
 *    rescues a carelessly cased string, and it is what lets "Prest0n" keep
 *    its capital on a card label (2026-09-02, plan §3.3: global.css
 *    lowercases EVERY element and buttons with !important; among two
 *    !important declarations the more specific selector wins — the same
 *    mechanism as the global sheet's own password exemption).
 *  - A DECISION disclosure that hides CONTROLS (`renderChoiceDisclosure`,
 *    "other ways ▾") is not a preview window: it never shows content, has
 *    no border, background, scroll or max-height, and its rows are pills +
 *    one eyebrow line each (owner ruling 2026-09-02, plan §9.5a).
 */

import {
  ATTACHMENT_ISSUE_FACTS,
  ATTACHMENT_ISSUE_WORDS,
  ATTACHMENT_SOURCE_WORDS,
  CARD_ATTACHMENT_CAP,
  CARD_ATTACHMENT_ISSUE_REASONS,
  CARD_ATTACHMENT_SOURCES,
  attachmentIssueWords,
  attachmentSourceWords,
  attachmentsBlockSend,
  issueStillDelivers,
  normalizeCardAttachment,
  normalizeCardAttachmentIssue,
  normalizeCardAttachmentIssues,
  normalizeCardAttachments,
} from '../../../types/emailAttachmentCard';
import type {
  AttachmentIssueFact,
  CardAttachment,
  CardAttachmentIssue,
  CardAttachmentIssueReason,
  CardAttachmentSource,
} from '../../../types/emailAttachmentCard';

/** Palette tokens — warm ink + gold, the connect-card convention. */
export const P0AC = {
  INK: '#1C1917',
  SUBTLE: '#78716C',
  FAINT: 'var(--brand-text-muted)',
  BORDER: '#E7E0D8',
  SURFACE: '#FAF8F5',
  GOLD: 'var(--brand-gold)',
  GOLD_DEEP: '#8A6D2F',
  GOLD_TINT: 'rgba(198,167,94,0.12)',
  GOLD_BORDER: 'rgba(198,167,94,0.4)',
  OK_INK: '#8A6D2F',
  OK_BG: '#FBF7EC',
  OK_BORDER: '#E9DCB8',
  WARN_INK: '#9A5B10',
  WARN_BG: '#FDF8F1',
  WARN_BORDER: '#EEDCC2',
} as const;

export function escapeHtml(text: string): string {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;');
}

/**
 * The ONE https gate for every url a kit card renders as a destination — a
 * pill's data attribute, an informational anchor's href (2026-09-02 review):
 * `/^https:\/\//` alone let a url with embedded credentials
 * (`https://user:pass@host/…`) ride into the DOM, behind a hostname label
 * that hid the userinfo. Requires https, a parseable url and NO userinfo.
 * Returns the caller's OWN string (trimmed, capped) — never the parser's
 * normalized form — so escaping and byte-identical rebuilds stay the
 * caller's. Anything else → ''.
 */
export function safeHttpsUrl(raw: unknown, maxLength = 500): string {
  const value = String(raw ?? '').trim();
  if (!/^https:\/\//i.test(value)) return '';
  try {
    const parsed = new URL(value);
    if (parsed.protocol !== 'https:' || parsed.username || parsed.password) return '';
  } catch {
    return '';
  }
  return value.slice(0, maxLength);
}

/**
 * The one scoped stylesheet. Emitted once per card (duplicates across cards
 * on a page are identical and idempotent). `.p0ac-btn:focus` (0-2-0) beats
 * the global `:focus` (0-1-0), so the mouse-click rectangle dies here.
 */
export function actionCardStyles(): string {
  return `
.p0ac-card{margin-top:2px;}
.p0ac-title{display:flex;align-items:center;gap:8px;font-weight:700;color:${P0AC.INK};font-size:0.95rem;line-height:1.4;}
.p0ac-title svg{flex-shrink:0;}
.p0ac-chips{display:flex;flex-wrap:wrap;gap:6px;margin-top:8px;}
.p0ac-chip{display:inline-flex;align-items:center;gap:5px;padding:3px 10px;border-radius:999px;font-size:0.72rem;font-weight:700;border:1px solid ${P0AC.BORDER};}
.p0ac-chip--ok{background:${P0AC.OK_BG};color:${P0AC.OK_INK};border-color:${P0AC.OK_BORDER};}
.p0ac-chip--neutral{background:${P0AC.SURFACE};color:${P0AC.INK};}
.p0ac-chip--muted{background:transparent;color:${P0AC.SUBTLE};}
.p0ac-chip--warn{background:${P0AC.WARN_BG};color:${P0AC.WARN_INK};border-color:${P0AC.WARN_BORDER};}
.p0ac-note{margin-top:10px;color:${P0AC.SUBTLE};font-size:0.76rem;line-height:1.5;}
.p0ac-btn{border-radius:999px;font-weight:700;cursor:pointer;letter-spacing:0.01em;border:none;transition:transform 140ms ease,box-shadow 140ms ease,background 140ms ease,border-color 140ms ease;}
.p0ac-btn--md{padding:9px 18px;font-size:0.82rem;min-height:40px;}
.p0ac-btn--sm{padding:6px 14px;font-size:0.78rem;min-height:32px;min-width:0;}
.p0ac-btn--primary{background:${P0AC.INK};color:#fff;}
.p0ac-btn--ghost{background:transparent;color:${P0AC.INK};border:1px solid ${P0AC.BORDER};}
.p0ac-btn:hover:not([disabled]){transform:translateY(-1px);box-shadow:0 3px 10px rgba(28,25,23,0.18);}
.p0ac-btn--ghost:hover:not([disabled]){border-color:rgba(198,167,94,0.55);background:#FBF7EE;}
.p0ac-btn:active:not([disabled]){transform:translateY(0);box-shadow:none;}
.p0ac-btn[disabled]{opacity:0.55;cursor:default;transform:none;box-shadow:none;}
.p0ac-card button:focus{outline:none;}
.p0ac-card button:focus-visible{outline:2px solid ${P0AC.GOLD};outline-offset:2px;}
.p0ac-detail{margin-top:10px;font-size:0.8rem;line-height:1.55;color:${P0AC.INK};}
.p0ac-body{margin-top:10px;padding-top:10px;border-top:1px solid ${P0AC.BORDER};font-size:0.84rem;line-height:1.6;color:${P0AC.INK};word-wrap:break-word;}
.p0ac-body-note{margin-top:6px;color:${P0AC.SUBTLE};font-size:0.72rem;font-style:italic;}
.p0ac-rows{margin-top:6px;}
.p0ac-rows--scroll{max-height:62vh;overflow-y:auto;overscroll-behavior:contain;-webkit-overflow-scrolling:touch;padding-right:6px;}
.p0ac-row{border-top:1px solid ${P0AC.BORDER};padding:12px 0;}
.p0ac-row--solo{border-top:none;padding:0;}
.p0ac-recips{display:inline-flex;flex-wrap:wrap;gap:4px;vertical-align:top;}
.p0ac-recip{display:inline-flex;align-items:center;padding:1px 8px;border-radius:999px;font-size:0.78rem;line-height:1.45;background:${P0AC.SURFACE};border:1px solid ${P0AC.BORDER};color:${P0AC.INK};}
.p0ac-jump{display:flex;flex-wrap:wrap;gap:6px;margin-top:10px;}
.p0ac-jump-note{align-self:center;color:${P0AC.SUBTLE};font-size:0.72rem;}
.p0ac-row:target,.p0ac-row--flash{background:${P0AC.GOLD_TINT};}
.p0ac-row-head{display:flex;align-items:center;gap:10px;justify-content:space-between;}
.p0ac-row-meta{min-width:0;font-size:0.82rem;color:${P0AC.INK};line-height:1.5;}
.p0ac-row-eyebrow{color:${P0AC.SUBTLE};font-size:0.72rem;}
.p0ac-meta-label{color:${P0AC.SUBTLE};}
.p0ac-row-note{color:${P0AC.WARN_INK};font-size:0.74rem;}
.p0ac-state{display:inline-flex;align-items:center;gap:5px;flex-shrink:0;font-size:0.76rem;font-weight:700;}
.p0ac-btnrow{display:flex;gap:8px;flex-shrink:0;}
.p0ac-actions{margin-top:12px;display:flex;gap:10px;}
.p0ac-footer{margin-top:14px;padding-top:12px;border-top:1px solid ${P0AC.BORDER};display:flex;align-items:center;gap:10px;flex-wrap:wrap;}
.p0ac-footer-help{color:${P0AC.SUBTLE};font-size:0.74rem;}
.p0ac-card,.p0ac-card *{text-transform:none;}
.p0ac-card button{text-transform:none !important;}
a.p0ac-btn{display:inline-flex;align-items:center;text-decoration:none;}
.p0ac-eyebrow{color:${P0AC.SUBTLE};font-size:0.72rem;line-height:1.4;}
.p0ac-alts{margin-top:10px;}
.p0ac-alt{display:flex;align-items:center;gap:10px;padding:6px 0;}
details.p0ac-disclosure{margin-top:10px;}
details.p0ac-disclosure>summary{list-style:none;display:inline-flex;align-items:center;cursor:pointer;}
details.p0ac-disclosure>summary::-webkit-details-marker{display:none;}
`.trim();
}

export type ChipTone = 'ok' | 'neutral' | 'muted' | 'warn';

/** A small status pill for the card header ("2 sent", "1 awaiting your call"). */
export function renderStatusChip(tone: ChipTone, label: string): string {
  return `<span class="p0ac-chip p0ac-chip--${tone}">${escapeHtml(label)}</span>`;
}

export type StateIcon = 'check' | 'arrow' | 'alert' | 'clock';

/** 12px inline state icon, stroke currentColor — rides state lines and
 *  titles, NEVER inside a decision button (label stays the sole child). */
export function renderStateIcon(icon: StateIcon): string {
  const paths: Record<StateIcon, string> = {
    check: '<polyline points="2.5 6.5 5 9 9.5 3.5"/>',
    arrow: '<line x1="2" y1="6" x2="10" y2="6"/><polyline points="6.5 2.5 10 6 6.5 9.5"/>',
    alert: '<line x1="6" y1="2.5" x2="6" y2="7"/><circle cx="6" cy="9.5" r="0.5"/>',
    clock: '<circle cx="6" cy="6" r="4.5"/><polyline points="6 3.5 6 6 8 7"/>',
  };
  return `<svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${paths[icon]}</svg>`;
}

/**
 * A decision pill. `label` is the button's SOLE child; `dataAttrs` is a
 * pre-escaped attribute string (`data-action-id="…" data-to="…" …`).
 * The `disabled` attribute lands right after the data attributes so the
 * pinned liveness regexes (`/…"[^>]*disabled/`) keep matching.
 */
export function renderPillButton(opts: {
  id: string;
  label: string;
  variant: 'primary' | 'ghost';
  size: 'md' | 'sm';
  disabled?: boolean;
  dataAttrs: string;
}): string {
  const id = escapeHtml(opts.id);
  return `<button id="${id}" data-button-id="${id}" ${opts.dataAttrs}${opts.disabled ? ' disabled' : ''} class="p0ac-btn p0ac-btn--${opts.size} p0ac-btn--${opts.variant}">${escapeHtml(opts.label)}</button>`;
}

/**
 * An informational LINK styled as a ghost pill — an `<a>`, never a
 * `<button>`: the delegated chat click handler dispatches only buttons, so
 * an anchor navigates natively (ensureLinksOpenInNewTab adds target/rel at
 * render). safeHttpsUrl only (https, no userinfo); anything else renders
 * nothing rather than a dead pill. Used for the "other ways" official links
 * on the connect card.
 */
export function renderLinkPill(opts: { href: string; label: string; size?: 'md' | 'sm' }): string {
  const href = safeHttpsUrl(opts.href);
  if (!href) return '';
  return `<a class="p0ac-btn p0ac-btn--${opts.size ?? 'sm'} p0ac-btn--ghost" href="${escapeHtml(href)}">${escapeHtml(opts.label)}</a>`;
}

export interface ChoiceAlternative {
  /** One eyebrow line beside the pill (model-authored on the connect card). */
  eyebrow: string;
  /** A rendered pill (renderPillButton / renderLinkPill) or nothing — an
   *  eyebrow-only row is legal; a pill with no action is not. */
  pillHtml?: string;
}

/**
 * "other ways ▾" — the ONE decision disclosure (owner ruling 2026-09-02,
 * plan §9.5a): a control that hides ALTERNATIVE CONTROLS, never content.
 * Count rule: 0 → nothing; exactly 1 → one flat row (no details); 2–4 → a
 * native `<details>` whose `<summary>` is a ghost pill, closed unless
 * `open`; more than 4 → the first 4. Each row is a pill + one eyebrow line,
 * nothing else — no border, background, scroll box or max-height (the
 * no-preview-windows pins forbid them). `<details>` needs no JS, so it
 * survives the injected-html lane, and every shipped transform leaves
 * `<summary>`/`<a>` untouched (they only rewrite `<button>`).
 */
export function renderChoiceDisclosure(opts: {
  alternatives: ReadonlyArray<ChoiceAlternative>;
  summaryLabel: (count: number) => string;
  open?: boolean;
}): string {
  const rows = (opts.alternatives || [])
    .filter((a) => a && (String(a.eyebrow || '').trim() || a.pillHtml))
    .slice(0, 4);
  if (rows.length === 0) return '';
  const rowsHtml = rows
    .map((a) => `<div class="p0ac-alt">${a.pillHtml || ''}<span class="p0ac-eyebrow">${escapeHtml(a.eyebrow)}</span></div>`)
    .join('\n    ');
  if (rows.length === 1) return `<div class="p0ac-alts">${rowsHtml}</div>`;
  return `<details class="p0ac-disclosure"${opts.open ? ' open' : ''}><summary class="p0ac-btn p0ac-btn--sm p0ac-btn--ghost">${escapeHtml(opts.summaryLabel(rows.length))}</summary><div class="p0ac-alts">
    ${rowsHtml}
  </div></details>`;
}

/**
 * NO PREVIEW WINDOWS (owner directive 2026-08-29). An action tile is a
 * SUMMARY and a CONTROL — who it goes to, what it is called, and the
 * buttons that decide it — never a document viewer. The old inset panel
 * showed a truncated fragment of the body in a cramped scrolling box: it
 * duplicated content the assistant already writes into its reply, and the
 * truncation made it actively misleading ("is that the whole email?").
 * The composed draft belongs in the message above the card, where it has
 * the full width and the house typography; the card decides it.
 *
 * Deliberately no `renderPreviewToggle` / `renderPreviewPanel` here — do
 * not reintroduce them. Detail LINES (why:/next:) use `renderDetailBlock`,
 * which is spacing only: no border, no background, no scroll box.
 */

/** A `label: value` metadata line (from:/to:/cc:/subject:/why:/next:).
 *  Value must be pre-escaped by the caller (it is user data). */
export function renderMetaRow(label: string, valueEscaped: string): string {
  return `<div><span class="p0ac-meta-label">${escapeHtml(label)}</span> ${valueEscaped}</div>`;
}

/**
 * The client twin of composioActions' `splitEmailList` — the SAME parse, so
 * the card can never show a different set of recipients than the send builds.
 * Kept here rather than imported because this module is the Firebase-free leaf
 * the chat reducer calls; the pair is pinned cross-tree in
 * `tests/orchestrator/` (the authorizeActionParity idiom).
 */
export function splitRecipients(value: unknown): string[] {
  if (Array.isArray(value)) return value.map((s) => String(s).trim()).filter(Boolean);
  if (typeof value === 'string' && value.trim()) {
    return value.split(/[,;]+/).map((s) => s.trim()).filter(Boolean);
  }
  return [];
}

/** Bytes as a card label. A local copy on purpose: `formatFileSize` lives in
 *  GlobalChatSystem/utils, and this module may not grow an import graph that
 *  the chat reducer has to carry. */
export function humanBytes(bytes: number): string {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return '';
  if (n < 1024) return `${Math.round(n)} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

/**
 * A recipient line (to:/cc:/bcc:). ONE recipient renders BYTE-IDENTICALLY to
 * `renderMetaRow(label, escapeHtml(raw))` — that equality is what lets the
 * single-email card keep its exact bytes through the renderer unification.
 * SEVERAL render as chips, all of them, never behind a disclosure: recipients
 * are addressing, not content, and the card is the whole truth about where
 * this is going (the same reasoning that put `cc` on the summary in the first
 * place, batchSendTile 2026-08-29).
 */
export function renderRecipientLine(label: string, raw: unknown): string {
  const list = splitRecipients(raw);
  if (list.length === 0) return '';
  if (list.length === 1) return renderMetaRow(label, escapeHtml(list[0]));
  const chips = list.map((r) => `<span class="p0ac-recip">${escapeHtml(r)}</span>`).join('');
  return renderMetaRow(label, `<span class="p0ac-recips">${chips}</span>`);
}

/**
 * The attachment vocabulary moved to src/types/emailAttachmentCard.ts when
 * attachments got producers (2026-09-17): the SERVER decides what is attached
 * and what is carried as a link, so the words and the reasons had to become a
 * client/server twin rather than a client-only declaration. Re-exported under
 * the same names so every import site — this module's renderers, the React
 * staged-send tile, the batch tile — is unchanged.
 */
export type {
  AttachmentIssueFact,
  CardAttachment,
  CardAttachmentIssue,
  CardAttachmentIssueReason,
  CardAttachmentSource,
};
export {
  ATTACHMENT_ISSUE_FACTS,
  ATTACHMENT_ISSUE_WORDS,
  ATTACHMENT_SOURCE_WORDS,
  CARD_ATTACHMENT_CAP,
  CARD_ATTACHMENT_ISSUE_REASONS,
  CARD_ATTACHMENT_SOURCES,
  attachmentIssueWords,
  attachmentSourceWords,
  attachmentsBlockSend,
  issueStillDelivers,
  normalizeCardAttachment,
  normalizeCardAttachmentIssue,
  normalizeCardAttachmentIssues,
  normalizeCardAttachments,
};

/** `attached:` — one chip per file: name, size, and where it came from when
 *  that is worth saying (a file from this conversation is not annotated — the
 *  person is looking at the conversation). Renders NOTHING when there is
 *  nothing attached, which is every staged email that names no file. */
export function renderAttachmentLine(attachments: ReadonlyArray<CardAttachment> | undefined): string {
  const files = (attachments || []).filter((a) => a && String(a.name || '').trim());
  if (files.length === 0) return '';
  const chips = files
    .map((a) => {
      const parts = [humanBytes(Number(a.size)), attachmentSourceWords(a.source)]
        .filter((v): v is string => !!v)
        .map((v) => ` · ${escapeHtml(v)}`)
        .join('');
      return `<span class="p0ac-recip">${escapeHtml(a.name)}${parts}</span>`;
    })
    .join('');
  return renderMetaRow('attached:', `<span class="p0ac-recips">${chips}</span>`);
}

/** The honest counterpart: every file that was named and is not riding as an
 *  attachment — whether it is being carried as a link instead, or is not going
 *  at all. An unrecognised reason renders nothing rather than a blank dash. */
export function renderAttachmentIssueLine(issues: ReadonlyArray<CardAttachmentIssue> | undefined): string {
  const rows = (issues || [])
    .map((i) => (i ? { name: String(i.name || '').trim(), words: attachmentIssueWords(i.reason) } : null))
    .filter((r): r is { name: string; words: string } => !!r && !!r.name && !!r.words);
  if (rows.length === 0) return '';
  const text = rows.map((r) => `${r.name} — ${r.words}`).join(' · ');
  return `<div class="p0ac-row-note">not attached: ${escapeHtml(text)}</div>`;
}

/**
 * The DRAFT BODY as the card's own flowing prose (one-tile directive,
 * 2026-08-29): full width, house typography, a hairline above — NOT a boxed
 * window, NOT a scroll pane, NOT a disclosure. Newlines become <br/> so
 * "Miles Maggio\nPrest0 Inc." keeps its line structure. `truncated` renders
 * an honest note for the rare over-cap body (what sends is always the full
 * verbatim params, server-side).
 */
export function renderBodyBlock(bodyRaw: string, truncated?: boolean): string {
  // \r\n and lone \r normalize to \n first (adversarial review 2026-08-29):
  // pasted Windows/legacy text must keep every line break on the "exactly
  // as it will send" surface, with no stray carriage returns.
  const body = escapeHtml(bodyRaw.replace(/\r\n?/g, '\n')).replace(/\n/g, '<br/>');
  const note = truncated
    ? `\n    <div class="p0ac-body-note">shown up to the display limit — the email sends in full, exactly as written.</div>`
    : '';
  return `<div class="p0ac-body">${body}${note}</div>`;
}

/** A detail section — spacing only, never a boxed window (see above). */
export function renderDetailBlock(linesHtml: string[]): string {
  if (linesHtml.length === 0) return '';
  return `<div class="p0ac-detail">
    ${linesHtml.join('\n    ')}
  </div>`;
}

/** The footer row: one action pill + a helper sentence (send-all, retry). */
export function renderFooterRow(opts: { buttonHtml: string; helperText: string }): string {
  return `<div class="p0ac-footer">
    ${opts.buttonHtml}
    <span class="p0ac-footer-help">${escapeHtml(opts.helperText)}</span>
  </div>`;
}

/**
 * The card shell. `rootAttrs` renders VERBATIM and FIRST after `<div ` —
 * the batch tile's `data-batch-root="…" data-batch-action-ids="…"` pair
 * must keep its whitespace adjacency (parseBatchTileHtml on already-shipped
 * bundles regexes exactly that shape). The stylesheet is the first child.
 */
export function renderCardShell(opts: {
  rootAttrs?: string;
  maxWidth?: number;
  title: string;
  titleInk?: string;
  titleIconSvg?: string;
  chipsHtml?: string;
  noteHtml?: string;
  bodyHtml: string;
}): string {
  const width = opts.maxWidth ?? 640;
  return `<div ${opts.rootAttrs ? `${opts.rootAttrs} ` : ''}class="p0ac-card" style="max-width:${width}px;">
  <style>${actionCardStyles()}</style>
  <div class="p0ac-title"${opts.titleInk ? ` style="color:${opts.titleInk};"` : ''}>${opts.titleIconSvg || ''}<span>${escapeHtml(opts.title)}</span></div>${opts.chipsHtml ? `\n  ${opts.chipsHtml}` : ''}${opts.noteHtml ? `\n  ${opts.noteHtml}` : ''}
  ${opts.bodyHtml}
</div>`;
}
